import React, { useEffect, useRef, useState } from "react";
import { get_questionnaires, bulkUploadThirdpartyQuestionsService } from "../../services";
import CommonModal from "../../Components/CommonModal";

// Custom-mode input + its Cancel button need a matching fixed height — a
// stylesheet specificity conflict elsewhere in the app (`.common_button
// button` beats `.cmn_btn`) makes CommonButton render taller than
// `.cmn-modal-input` unless pinned explicitly. Same values used in
// CreateTPRMQuestionModal.js's equivalent fields for visual parity.
const CUSTOM_FIELD_HEIGHT = 38;
const CUSTOM_INPUT_STYLE = { height: CUSTOM_FIELD_HEIGHT, padding: "0 12px" };
const CUSTOM_CANCEL_BTN_STYLE = { height: CUSTOM_FIELD_HEIGHT };

// Questionnaire Name now matches CreateTPRMQuestionModal.js's toggle pattern
// exactly (same fields=/onChange/suffixButton/formRef combination — see that
// file's own header comment for the full reasoning). No optionsReady gating
// needed here unlike that file: this modal has no "edit" mode, so there's no
// legacy pre-selected value that could race against the questionnaires
// fetch — the dropdown just shows its own "Loading questionnaires..."
// placeholder (and disables) until the fetch resolves, same as before.
function BulkUploadQuestionsModal({ show, onHide, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [resMsg, setResMsg] = useState("");

    const [uploadFile, setUploadFile] = useState(null);
    const [fileError, setFileError] = useState("");

    const [questionnaireOptions, setQuestionnaireOptions] = useState([]);
    const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
    const [isCustomQuestionnaire, setIsCustomQuestionnaire] = useState(false);

    const fileInputRef = useRef(null);
    const formApiRef = useRef(null);
    // Tracks the last real (non-"custom") dropdown value, so Cancel restores
    // whatever the user had picked before switching to custom mode instead
    // of always blanking it back to the placeholder.
    const lastQuestionnaireValueRef = useRef("");

    useEffect(() => {
        if (!show) return;
        setSubmitError("");
        setResMsg("");
        setUploadFile(null);
        setFileError("");
        setIsCustomQuestionnaire(false);
        lastQuestionnaireValueRef.current = "";
        if (fileInputRef.current) fileInputRef.current.value = "";

        const fetchQuestionnaires = async () => {
            setLoadingQuestionnaires(true);
            try {
                const response = await get_questionnaires();
                const data = response?.statusCode?.response_data?.data;
                if (data && data.questionnaires) {
                    const options = data.questionnaires.map((q) => ({ value: q.name, label: q.name }));
                    options.push({ value: "custom", label: "Custom (Enter your own)" });
                    setQuestionnaireOptions(options);
                }
            } catch (error) {
                console.error("Error fetching questionnaires:", error);
            } finally {
                setLoadingQuestionnaires(false);
            }
        };
        fetchQuestionnaires();
    }, [show]);

    const handleClose = () => {
        setSubmitError("");
        onHide?.();
    };

    const handleQuestionnaireCancel = () => {
        setIsCustomQuestionnaire(false);
        formApiRef.current?.setValue("questionnaire_name", lastQuestionnaireValueRef.current || "");
    };

    const handleFormSubmit = async (data) => {
        if (!uploadFile) {
            setFileError("Please select a file to upload.");
            return;
        }
        setFileError("");

        const finalQuestionnaireName = isCustomQuestionnaire ? (data.questionnaire_name_custom || "").trim() : data.questionnaire_name;

        setLoading(true);
        setSubmitError("");
        setResMsg("");

        try {
            const response = await bulkUploadThirdpartyQuestionsService(uploadFile, finalQuestionnaireName);

            const successMessage = response?.statusCode?.response_data?.message || response?.statusCode?.message || response?.data?.message;
            const errorMessage = response?.errors?.message || response?.error?.message || response?.statusCode?.error?.message;

            if (successMessage && !errorMessage) {
                setResMsg(successMessage || "Questions uploaded successfully!");
                setUploadFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                setTimeout(() => {
                    onSuccess?.();
                    handleClose();
                }, 1500);
            } else {
                setSubmitError(errorMessage || "Failed to upload questions. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading questions:", error);
            setSubmitError("Failed to upload questions. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const questionnaireField = isCustomQuestionnaire
        ? {
            name: "questionnaire_name_custom",
            label: "Questionnaire Name (Optional)",
            placeholder: "e.g., Default, Cloud Security Assessment, SOC2 Audit",
            validate: (v) => !v || v.trim().toLowerCase() !== "default" || "Questionnaire name cannot be 'Default'. Please choose a different name.",
            style: CUSTOM_INPUT_STYLE,
            suffixButton: { text: "Cancel", onClick: handleQuestionnaireCancel, style: CUSTOM_CANCEL_BTN_STYLE },
        }
        : {
            name: "questionnaire_name",
            label: "Questionnaire Name (Optional)",
            type: "select",
            options: questionnaireOptions,
            disabled: loadingQuestionnaires,
            placeholder: loadingQuestionnaires ? "Loading questionnaires..." : "Select Questionnaire (Default)",
            onChange: (v) => { if (v === "custom") setIsCustomQuestionnaire(true); else lastQuestionnaireValueRef.current = v; },
        };

    const FIELDS = [questionnaireField];
    const defaultValues = { questionnaire_name: "", questionnaire_name_custom: "" };

    return (
        <CommonModal
            show={show}
            onHide={handleClose}
            title="Bulk Upload Questions"
            size="lg"
            error={submitError}
            fields={FIELDS}
            defaultValues={defaultValues}
            onSubmit={handleFormSubmit}
            formRef={formApiRef}
            buttons={[
                { text: "Cancel", variant: "secondary", onClick: handleClose, disabled: loading },
                { text: `Upload`, variant: "primary", type: "submit", loading, loadingText: "Uploading…" },
            ]}
        >
            <p style={{ fontSize: 13, opacity: 0.75, marginTop: 8 }}>
                Leave empty or select 'Default' to add to the Default questionnaire.
            </p>

            <div className="mb-3">
                <label className="font_500 d-block mb-1">Upload File (Excel/CSV)</label>
                <div className="common_input">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => {
                            setUploadFile(e.target.files[0]);
                            setFileError("");
                        }}
                    />
                </div>
                {fileError && <div className="text-danger mt-1">{fileError}</div>}
            </div>

            {resMsg && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <strong>Success!</strong> {resMsg}
                    <button type="button" className="btn-close" onClick={() => setResMsg("")}></button>
                </div>
            )}
        </CommonModal>
    );
}

export default BulkUploadQuestionsModal;
